const data = {
  slug: "growth-has-stalled",
  number: "01",
  title: "Growth Has Stalled",
  intro: [
    "Everyone is moving. The business isn't. More campaigns. More meetings. More pressure on sales. More initiatives across the organisation, but revenue, demand, market share or profitability is no longer moving as it should.",
    "When growth stalls, the instinct is often to increase activity. The real need is to understand what the activity is failing to change.",
  ],
  sections: [
    {
      kicker: "What it looks like",
      heading: "Growth does not always stop dramatically.",
      body: [
        "The business continues operating. Teams remain busy. Budgets are still being deployed. Targets may even be met occasionally. But the underlying trajectory has weakened. You may be seeing:",
      ],
      items: [
        "Revenue growing more slowly than the market",
        "Greater marketing expenditure producing weaker returns",
        "Sales teams working harder to deliver the same outcome",
        "Distribution expanding without enough throughput",
        "Customer acquisition becoming more expensive",
        "Repeat purchase or retention weakening",
        "Market share becoming difficult to defend",
        "New launches failing to create sufficient momentum",
        "Revenue increasing while margins decline",
        "Leadership repeatedly revising targets without changing the underlying system",
      ],
    },
    {
      kicker: "The real problem",
      heading: "The visible problem may not be the real one.",
      body: [
        "Stalled growth is usually a symptom. Sales may blame marketing. Marketing may blame the proposition. The organisation may blame the market. Leadership may blame execution.",
        "Growth can stall because of one constraint, or because several parts of the business are no longer reinforcing one another. The challenge is not identifying everything that could be better. It is identifying what is preventing growth from moving now.",
      ],
    },
    {
      kicker: "Possible causes",
      heading: "What may actually be holding growth back",
      body: ["The constraint may sit anywhere in the growth system."],
      items: [
        "The market has changed, customer behaviour, category dynamics or competition may have moved faster than the business.",
        "The proposition has weakened, the offer may no longer be distinctive, relevant or valuable enough to create preference.",
        "Marketing is generating activity, not demand, campaigns run without strengthening memory, consideration or commercial response.",
        "Sales is solving the wrong problem, pressure cannot compensate for weak demand, poor access or an ineffective proposition.",
        "Distribution is present but not productive, reach without visibility, availability, throughput or channel economics.",
        "Growth has become too expensive, acquisition costs, discounting or complexity are weakening the model.",
        "The portfolio lacks focus, too many brands, products or initiatives competing for limited investment and attention.",
        "The organisation is fragmented, functions pursuing different priorities, metrics and versions of the problem.",
        "Execution has lost discipline, the strategy may be right, but ownership, sequencing and corrective action are weak.",
      ],
    },
    {
      kicker: "Common mistakes",
      heading: "What businesses commonly get wrong",
      body: ["When growth slows, businesses often respond by doing more of what is already not working."],
      items: [
        "Increase marketing before fixing the proposition",
        "Push sales harder without resolving demand or distribution",
        "Expand into more markets before fixing the existing model",
        "Add products without making portfolio choices",
        "Reduce prices to force volume",
        "Change agencies or partners without changing direction",
        "Launch multiple initiatives at once",
        "Produce another strategy document",
      ],
    },
    {
      kicker: "Our approach",
      heading: "How SWITCH diagnoses stalled growth",
      body: [
        "Before recommending action, we identify where growth is breaking down. The diagnostic is shaped around the business and may examine:",
      ],
      items: [
        "Business performance",
        "Market and category",
        "Proposition and portfolio",
        "Brand, marketing and digital",
        "Sales and distribution",
        "Economics and operations",
        "Organisation and execution",
      ],
    },
    {
      kicker: "The outputs",
      heading: "What the business receives",
      body: [
        "A diagnosis is useful only when it produces decisions. The engagement results in two connected outputs.",
        "Growth Diagnostic Report, a clear view of what is constraining growth, what is a symptom and what is a root cause, where value is being lost, which opportunities are credible, and what to stop, fix or prioritise.",
        "Growth Action Plan, a practical roadmap defining the priorities, the choices leadership must make, the sequence of interventions, the capabilities required, ownership across teams and partners, measures of progress, and the path from decision to implementation.",
      ],
    },
    {
      kicker: "Potential interventions",
      heading: "What we might change",
      body: [
        "The solution depends on what the diagnosis reveals. SWITCH may lead one intervention or bring several together, the business is never forced into a standard package.",
      ],
      items: [
        "Refocusing the growth strategy",
        "Rebuilding the proposition",
        "Restructuring the portfolio",
        "Improving marketing effectiveness",
        "Strengthening sales and distribution",
        "Restoring profitability",
        "Redesigning processes and systems",
        "Aligning the organisation",
        "Establishing a growth operating system",
      ],
    },
    {
      kicker: "From diagnosis to execution",
      heading: "We stay to make it happen",
      body: [
        "Knowing why growth has stalled is only the beginning. SWITCH works alongside your leadership team, internal functions and existing partners to move the priorities into action, and remains involved until the agreed priorities are implemented.",
      ],
    },
  ],
};

export default data;
